import { ChevronLeft, ChevronRight, CircleAlert, CircleCheck, Hammer, LoaderCircle, Search } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { Button, StatusMessage, TextField } from '@krp/ui-kit'
import type { QueryView } from '@krp/client-runtime'

import { QueryState } from '@krp/sale-ui'
import '../../../shared/ui/aqsi.css'
import { presenceOf, probeStatusLine } from '../model/selectors'
import type { AqsiGoodsPage, AqsiProbeStatus } from '../model/types'

export interface ProbeSearchPanelProps {
  /** Состояние индекса: ответ службы (`none | building | ready | failed`). */
  status: AqsiProbeStatus | null
  statusQuery: QueryView
  /** Идёт запрос «Перестроить индекс». */
  rebuilding: boolean
  onRebuild: () => void
  /** Что искали и что нашли; страница — с 1. */
  term: string
  page: number
  result: AqsiGoodsPage | null
  searchQuery: QueryView
  onSearch: (text: string) => void
  onPage: (page: number) => void
  /** Открыть окно товара источника; нет — название товара просто текст. */
  onOpenGood?: (goodId: string) => void
}

const KIND_ICONS: Readonly<Record<'ok' | 'wait' | 'none' | 'failed', ReactNode>> = {
  ok: <CircleCheck size={14} aria-hidden="true" />,
  wait: <LoaderCircle size={14} aria-hidden="true" />,
  none: <CircleAlert size={14} aria-hidden="true" />,
  failed: <CircleAlert size={14} aria-hidden="true" />,
}

/**
 * «Поиск в AQSI» (RP-SE12) — проверочная вкладка. Ищет единая служба поиска, но не по общему каталогу, а по ОТДЕЛЬНОЙ копии данных AQSI,
 * построенной из снимка кабинета: прямого поиска у AQSI нет — его API не ищет по строке. Индекс перестраивается сам, когда снимок меняется,
 * и по кнопке «Перестроить индекс» (кабинет при этом не перечитывается: снимок обновляет кнопка «Загрузить товары из кабинета»).
 */
export function ProbeSearchPanel(props: ProbeSearchPanelProps) {
  const { status, statusQuery, rebuilding, onRebuild, term, page, result, searchQuery, onSearch, onPage, onOpenGood } = props
  const [text, setText] = useState(term)
  const trimmed = text.trim()
  const line = probeStatusLine(status)
  const building = status?.state === 'building'
  const searching = searchQuery.status === 'loading' || searchQuery.refreshing
  const canSearch = status !== null && status.state !== 'none' && !searching && trimmed !== '' && trimmed.length <= 128
  return (
    <section className="aqsi-form" aria-labelledby="aqsi-probe-title" data-testid="aqsi-probe">
      <div className="aqsi-toolbar">
        <h3 id="aqsi-probe-title">Поиск в AQSI</h3>
        <Button
          icon={<Hammer size={14} aria-hidden="true" />}
          busy={rebuilding}
          disabled={rebuilding || building || status === null || status.latestSnapshotAt === null}
          onClick={onRebuild}
        >
          Перестроить индекс
        </Button>
      </div>
      <p className="aqsi-card__state" role="status" data-testid="aqsi-probe-status" data-state={status?.state ?? 'unknown'}>
        {KIND_ICONS[line.kind]}
        {line.text}
      </p>
      <QueryState query={statusQuery} what="Состояние индекса" emptyText="" onRetry={() => undefined} />
      <p className="aqsi-caption">
        Ищет единая служба поиска по отдельной копии данных AQSI. Это проверочная вкладка: общий каталог и РМК ищут по общему каталогу.
      </p>
      <form
        className="aqsi-toolbar"
        onSubmit={(event) => {
          event.preventDefault()
          if (canSearch) onSearch(trimmed)
        }}
      >
        <TextField label="Строка поиска" type="search" value={text} onChange={setText} placeholder="Наименование, артикул или штрихкод" />
        <Button type="submit" icon={<Search size={14} aria-hidden="true" />} busy={searching} disabled={!canSearch}>
          Найти
        </Button>
      </form>
      {searching && <StatusMessage kind="loading" title="Ищем в индексе…" />}
      {!searching && searchQuery.status !== 'idle' && searchQuery.status !== 'ready' && searchQuery.status !== 'empty' && (
        <QueryState query={searchQuery} what="Поиск" emptyText="" onRetry={() => onSearch(term)} />
      )}
      {!searching && result !== null && term !== '' && <ProbeResult result={result} page={page} onPage={onPage} {...(onOpenGood === undefined ? {} : { onOpenGood })} />}
    </section>
  )
}

function ProbeResult({
  result,
  page,
  onPage,
  onOpenGood,
}: {
  result: AqsiGoodsPage
  page: number
  onPage: (page: number) => void
  onOpenGood?: (goodId: string) => void
}) {
  if (result.items.length === 0 && page === 1) return <StatusMessage kind="empty" title="В индексе ничего не найдено" />
  const hasMore = result.total > (page - 1) * result.pageSize + result.items.length
  return (
    <>
      <table className="aqsi-table">
        <thead>
          <tr>
            <th>Товар</th>
            <th>Артикул</th>
            <th>Цена</th>
            <th>Штрихкоды</th>
            <th>Состояние</th>
          </tr>
        </thead>
        <tbody>
          {result.items.map((good) => {
            const state = presenceOf(good.missingSince, good.invalidReason)
            return (
              <tr key={good.goodId}>
                <td data-label="Товар">
                  {onOpenGood === undefined ? (
                    good.name
                  ) : (
                    <button
                      type="button"
                      className="aqsi-good-link"
                      aria-label={`Открыть карточку «${good.name}» в AQSI`}
                      onClick={() => onOpenGood?.(good.goodId)}
                    >
                      {good.name}
                    </button>
                  )}
                </td>
                <td data-label="Артикул">{good.sku ?? '—'}</td>
                <td data-label="Цена" data-numeric="">
                  {good.price ?? '—'}
                </td>
                <td data-label="Штрихкоды">{good.barcodes.length > 0 ? good.barcodes.join(', ') : '—'}</td>
                <td data-label="Состояние">{state.state}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <nav className="aqsi-card__actions" aria-label="Страницы результатов поиска">
        <Button icon={<ChevronLeft size={14} aria-hidden="true" />} disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Назад
        </Button>
        <span>Страница {page}</span>
        <Button icon={<ChevronRight size={14} aria-hidden="true" />} disabled={!hasMore} onClick={() => onPage(page + 1)}>
          Дальше
        </Button>
      </nav>
    </>
  )
}
